import { Button, Card, Col, Form, Input, Row, Select, Space, theme } from 'antd';
import { useEffect, useState } from 'react';
import { computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@utils/property';
import './InvitationUsersFilterPanel.less';

const VISIBLE_FIELD_COUNT = 4;

const EXPERT_LEVEL_OPTIONS = [
    { value: 6, label: 'VIP★★★★★' },
    { value: 5, label: 'VIP★★★★' },
    { value: 4, label: 'VIP★★★' },
    { value: 3, label: 'VIP★★' },
    { value: 2, label: 'VIP★' },
    { value: 1, label: '专家' },
    { value: 10, label: '企业VIP' },
    { value: 11, label: '企业人员' },
    { value: 12, label: '健康界工作人员' },
    { value: 0, label: '普通用户' },
];

const HAS_INVITATION_ORG_OPTIONS = [
    { value: 1, label: '有邀请方' },
    { value: 2, label: '独家邀请方' },
    { value: 0, label: '无邀请方' },
];

const HAS_REMARKS_OPTIONS = [
    { value: 1, label: '有嘉宾备注' },
    { value: 0, label: '无嘉宾备注' },
];

export default function InvitationUsersFilterPanel({ filter, onFinish, onReset }) {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        form.setFieldsValue({
            username: filter.username,
            unitName: filter.unitName,
            unitOffices: filter.unitOffices,
            unitPost: filter.unitPost,
            expertLevels: filter.expertLevels,
            invitationAcceptStatus: filter.invitationAcceptStatus,
            hasInvitationOrg: filter.hasInvitationOrg,
            invitationOrgKeywords: filter.invitationOrgKeywords,
            hasRemarks: filter.hasRemarks,
            remarksKeyword: filter.remarksKeyword,
        });
        // 命中扩展字段则默认展开
        if (
            filter.expertLevels !== undefined
            || filter.invitationAcceptStatus !== undefined
            || filter.hasInvitationOrg !== undefined
            || (filter.invitationOrgKeywords !== undefined && filter.invitationOrgKeywords !== '')
            || filter.hasRemarks !== undefined
            || (filter.remarksKeyword !== undefined && filter.remarksKeyword !== '')
        ) {
            setExpanded(true);
        }
    }, [filter, form]);

    const handleReset = () => {
        form.resetFields();
        setExpanded(false);
        onReset();
    };

    // 展开后的可见字段数：4 基础 + 4 展开（嘉宾级别/任务状态/有无邀请方/有无嘉宾备注）+ 邀请方、嘉宾备注关键字（按条件显隐）
    const computeExpandedCount = (hasInvitationOrg, hasRemarks) => {
        let count = VISIBLE_FIELD_COUNT + 4;
        if (hasInvitationOrg !== 0) count += 1;
        if (hasRemarks !== 0) count += 1;
        return count;
    };

    return (
        <Card
            size="small"
            className="invitation-users-filter-panel"
            style={{
                marginBottom: token.marginLG,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillAlter,
            }}
        >
            <Form {...filterFormItemLayout} form={form} onFinish={onFinish} autoComplete="off" requiredMark={false}>
                <Row gutter={[16, 16]}>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="嘉宾姓名" name="username" style={filterFormItemStyle}>
                            <Input allowClear placeholder="嘉宾姓名" autoComplete="off" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="单位" name="unitName" style={filterFormItemStyle}>
                            <Input allowClear placeholder="单位名称关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="科室/部门" name="unitOffices" style={filterFormItemStyle}>
                            <Input allowClear placeholder="所在科室或部门关键字" />
                        </Form.Item>
                    </Col>

                    <Col {...filterFormGridLayout}>
                        <Form.Item label="职务" name="unitPost" style={filterFormItemStyle}>
                            <Input allowClear placeholder="职务关键字" />
                        </Form.Item>
                    </Col>

                    {expanded && (
                        <>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="嘉宾级别" name="expertLevels" style={filterFormItemStyle}>
                                    <Select mode="multiple" maxTagCount="responsive" options={EXPERT_LEVEL_OPTIONS} placeholder="请选择嘉宾级别" allowClear />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="嘉宾任务状态" name="invitationAcceptStatus" style={filterFormItemStyle}>
                                    <Select placeholder="请选择嘉宾任务接受状态" allowClear>
                                        <Select.Option value={0}>未确认</Select.Option>
                                        <Select.Option value={1}>已确认</Select.Option>
                                        <Select.Option value={2}>已拒绝</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="有无邀请方" name="hasInvitationOrg" style={filterFormItemStyle}>
                                    <Select options={HAS_INVITATION_ORG_OPTIONS} placeholder="请选择有无邀请方" allowClear />
                                </Form.Item>
                            </Col>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasInvitationOrg !== cur.hasInvitationOrg}>
                                {({ getFieldValue }) =>
                                    getFieldValue('hasInvitationOrg') !== 0 ? (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="邀请方" name="invitationOrgKeywords" style={filterFormItemStyle} preserve={false}>
                                                <Input allowClear placeholder="请输入邀请方" />
                                            </Form.Item>
                                        </Col>
                                    ) : null
                                }
                            </Form.Item>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="有无嘉宾备注" name="hasRemarks" style={filterFormItemStyle}>
                                    <Select options={HAS_REMARKS_OPTIONS} placeholder="请选择有无嘉宾备注" allowClear />
                                </Form.Item>
                            </Col>

                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasRemarks !== cur.hasRemarks}>
                                {({ getFieldValue }) =>
                                    getFieldValue('hasRemarks') !== 0 ? (
                                        <Col {...filterFormGridLayout}>
                                            <Form.Item label="嘉宾备注" name="remarksKeyword" style={filterFormItemStyle} preserve={false}>
                                                <Input allowClear placeholder="嘉宾备注关键字" />
                                            </Form.Item>
                                        </Col>
                                    ) : null
                                }
                            </Form.Item>
                        </>
                    )}

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasInvitationOrg !== cur.hasInvitationOrg || prev.hasRemarks !== cur.hasRemarks}>
                        {({ getFieldValue }) => {
                            const visibleCount = expanded ? computeExpandedCount(getFieldValue('hasInvitationOrg'), getFieldValue('hasRemarks')) : VISIBLE_FIELD_COUNT;
                            const buttonColLayout = computeButtonColLayout(visibleCount);
                            return (
                                <Col {...buttonColLayout}>
                                    <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                        <Space size="small">
                                            <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                                检索
                                            </Button>

                                            <Button type="default" onClick={handleReset}>
                                                重置
                                            </Button>

                                            <Button
                                                type="link"
                                                size="small"
                                                aria-expanded={expanded}
                                                onClick={() => {
                                                    setExpanded(prev => {
                                                        if (prev) {
                                                            form.setFieldsValue({
                                                                expertLevels: undefined,
                                                                invitationAcceptStatus: undefined,
                                                                hasInvitationOrg: undefined,
                                                                invitationOrgKeywords: undefined,
                                                                hasRemarks: undefined,
                                                                remarksKeyword: undefined,
                                                            });
                                                        }
                                                        return !prev;
                                                    });
                                                }}
                                                style={{ fontSize: 12, color: token.colorTextSecondary }}
                                            >
                                                {expanded ? (
                                                    <>
                                                        收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                                    </>
                                                ) : (
                                                    <>
                                                        展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                                    </>
                                                )}
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Col>
                            );
                        }}
                    </Form.Item>
                </Row>
            </Form>
        </Card>
    );
}
